import { Col, Form, Row } from 'react-bootstrap';
import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import ClientOnExcursionService from '../../../repository/clientOnExcursionRepository/ClientOnExcursionRepository';
import ExcursionService from '../../../repository/tourRepository/ExcursionRepository';
import { strings } from '../../../localization/Localization';

const AddClientToExcursionForm = (props) => {
  const [allExcursions, setAllExcursions] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [clientData, setClientData] = useState(props.client);
  const [excursionSearchTerm, setExcursionSearchTerm] = useState('');
  const [showExcursionDropdown, setShowExcursionDropdown] = useState(false);

  useEffect(() => {
    getAllOngoingExcursions();
    setClientData(props.client);
  }, [props.client]);

  const getAllOngoingExcursions = () => {
    try {
      ExcursionService.getOngoingExcursions().then((data) => {
        setAllExcursions(data.data);
      });
    } catch (error) {
      console.error("Error fetching excursions:", error);
    }
  };

  const schema = yup.object().shape({
    excursion: yup.object().required(strings.requiredField),
    discount: yup.number().required(strings.requiredField),
    points: yup.boolean().required(strings.requiredField),
    startingPoint: yup.string().required(strings.requiredField),
    note: yup.string().required(strings.requiredField),
    travelingDocument: yup.string().required(strings.requiredField),
    confirmedReservation: yup.boolean().required(strings.requiredField),
    extraExpenses: yup.number().required(strings.requiredField),
    seatNumber: yup.number().required(strings.requiredField),
    anotherAgency: yup.boolean().required(strings.requiredField),
  });

  const calculatePrice = (values) => {
    if (!values.excursion) return 0;
    const excursionPrice = values.excursion.price || 0;
    const excursionDiscount = values.excursion.discount || 0;
    const userDiscount = parseFloat(values.discount) || 0;
    const extraExpenses = parseFloat(values.extraExpenses) || 0;
    let pointsDeduction = 0;
    if (values.points && clientData && clientData.points) {
      pointsDeduction = clientData.points;
    }
    // Total price = Excursion price - Excursion discount - discount + extra expenses - points (if used)
    return excursionPrice - excursionDiscount - userDiscount + extraExpenses - (values.points ? pointsDeduction : 0);
  };

  const onFormSubmit = async (values) => {
    const { excursion, discount, points, startingPoint, note, travelingDocument, confirmedReservation, extraExpenses, seatNumber, anotherAgency } = values;    const client = clientData;

    if (!excursion || !client) {
      errorAlert(strings.missingEorC);
      return;
    }

    try {
      // 1. Add excursion to client
      await addExcursionToClient(client, excursion, discount, travelingDocument, startingPoint, note, points, confirmedReservation, extraExpenses, seatNumber, anotherAgency);
      // 2. Refresh client data to get updated points
      const updatedClient = await props.fetchClientForPointsUpdate(client.id);
      if (updatedClient) {
        setClientData(updatedClient);
      }

      // 3. Refresh excursion history
      props.getExcursionHistory();

      // Show success message
      addSuccessfulAlert(strings.successfullyAddedExcursion);
    } catch (error) {
      console.error("Error in form submission:", error);
      errorAlert();
    }
  };

  const addSuccessfulAlert = (message) => {
    Swal.fire({
      icon: 'success',
      title: message,
    });
  };

  const errorAlert = (message = strings.somethingWentWrong) => {
    Swal.fire({
      title: message,
      icon: "error",
      dangerMode: true
    });
  };

  const addExcursionToClient = (client, excursion, discount, travelingDocument, startingPoint, note, points, confirmedReservation, extraExpenses, seatNumber, anotherAgency) => {
    return ClientOnExcursionService.addClientOnExcursion(client, excursion, discount, travelingDocument, startingPoint, note, points, confirmedReservation, extraExpenses, seatNumber, anotherAgency)
      .catch((error) => {
        console.error("API Error:", error.response?.data || error.message);
        throw error;
      });
  };

  const filteredExcursions = allExcursions.filter(excursion =>
    excursion.name.toLowerCase().includes(excursionSearchTerm.toLowerCase()) ||
    excursion.dateFrom.toLowerCase().includes(excursionSearchTerm.toLowerCase()) ||
    excursion.price.toString().includes(excursionSearchTerm)
  );

  const handleExcursionSelect = (excursion, setFieldValue) => {
    setFieldValue("excursion", excursion);
    setExcursionSearchTerm(excursion.name);
    setShowExcursionDropdown(false);
  };

  const handleExcursionInputChange = (e, setFieldValue) => {
    const value = e.target.value;
    setExcursionSearchTerm(value);
    setShowExcursionDropdown(true);

    // Clear selected excursion if input is cleared
    if (value === '') {
      setFieldValue("excursion", null);
    }
  };

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onFormSubmit}
      initialValues={{
        excursion: null,
        client: clientData,
        discount: 0,
        points: false,
        startingPoint: '/',
        note: '/',
        travelingDocument: '/',
        confirmedReservation: false,
        extraExpenses: 0,
        seatNumber: 0,
        anotherAgency: false,
      }}
      enableReinitialize={true}
    >
      {({ handleSubmit, handleChange, values, setFieldValue, touched, errors }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          const price = calculatePrice(values);
          setTotalPrice(price >= 0 ? price : 0);
        }, [values.excursion, values.discount, values.points, values.extraExpenses, clientData.points]);

        return (
          <div onClick={() => setShowExcursionDropdown(false)}>
            <Form noValidate onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}>
              <h4 className="m-0">{strings.addNewExcursion}</h4>
              <hr className="m-1" />

              <Form.Group as={Col} md="12" controlId="validationFormik_excursion" className="position-relative">
                <Form.Label>{strings.selectExcursion}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={strings.excursion}
                  value={excursionSearchTerm}
                  onChange={(e) => handleExcursionInputChange(e, setFieldValue)}
                  onFocus={() => setShowExcursionDropdown(true)}
                  isInvalid={touched.excursion && !!errors.excursion}
                  autoComplete="off"
                />

                {showExcursionDropdown && filteredExcursions.length > 0 && (
                  <div
                    className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                    style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                  >
                    {filteredExcursions.map((excursion) => (
                      <div
                        key={excursion.id}
                        className="p-2 border-bottom hover-bg-light cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleExcursionSelect(excursion, setFieldValue)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        <div className="fw-bold">{excursion.name}</div>
                        <small className="text-muted">
                          {strings.price}: {excursion.price} - {strings.discount}: {excursion.discount} - {strings.dateFrom}: {excursion.dateFrom}
                        </small>
                      </div>
                    ))}
                  </div>
                )}

                <Form.Control.Feedback type="invalid">{errors.excursion}</Form.Control.Feedback>
              </Form.Group>
              <Row className="mt-2">
                <Col md={3} className="mb-3">
                  <Form.Group controlId="validationFormik_discount">
                    <Form.Label>{strings.discount}</Form.Label>
                    <Form.Control
                      type="number"
                      name="discount"
                      value={values.discount}
                      onChange={handleChange}
                      isValid={touched.discount && !errors.discount}
                      isInvalid={touched.discount && !!errors.discount}
                    />
                    <Form.Control.Feedback type="invalid">{errors.discount}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group controlId="validationFormik_travelingDocument">
                    <Form.Label>{strings.travelingDocument}</Form.Label>
                    <Form.Control
                      type="text"
                      name="travelingDocument"
                      value={values.travelingDocument}
                      onChange={handleChange}
                      isValid={touched.travelingDocument && !errors.travelingDocument}
                      isInvalid={touched.travelingDocument && !!errors.travelingDocument}
                    />
                    <Form.Control.Feedback type="invalid">{errors.travelingDocument}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group controlId="validationFormik_startingPoint">
                    <Form.Label>{strings.startingPoint}</Form.Label>
                    <Form.Control
                      type="text"
                      name="startingPoint"
                      value={values.startingPoint}
                      onChange={handleChange}
                      isValid={touched.startingPoint && !errors.startingPoint}
                      isInvalid={touched.startingPoint && !!errors.startingPoint}
                    />
                    <Form.Control.Feedback type="invalid">{errors.startingPoint}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3 mt-4 pt-2">
                  <Form.Group controlId="validationFormik_points">
                    <Form.Check
                      type="checkbox"
                      label={`${strings.usePoints} (${clientData.points || 0})`}
                      name="points"
                      checked={values.points}
                      onChange={() => setFieldValue("points", !values.points)}
                      isInvalid={touched.points && !!errors.points}
                    />
                    <Form.Control.Feedback type="invalid">{errors.points}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group controlId="validationFormik_note">
                    <Form.Label>{strings.note}</Form.Label>
                    <Form.Control
                      type="text"
                      name="note"
                      value={values.note}
                      onChange={handleChange}
                      isValid={touched.note && !errors.note}
                      isInvalid={touched.note && !!errors.note}
                    />
                    <Form.Control.Feedback type="invalid">{errors.note}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group controlId="validationFormik_extraExpenses">
                    <Form.Label>{strings.extraExpenses}</Form.Label>
                    <Form.Control
                      type="number"
                      name="extraExpenses"
                      value={values.extraExpenses}
                      onChange={handleChange}
                      isValid={touched.extraExpenses && !errors.extraExpenses}
                      isInvalid={touched.extraExpenses && !!errors.extraExpenses}
                    />
                    <Form.Control.Feedback type="invalid">{errors.extraExpenses}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3">
                  <Form.Group controlId="validationFormik_seatNumber">
                    <Form.Label>{strings.seatNumber}</Form.Label>
                    <Form.Control
                      type="text"
                      name="seatNumber"
                      value={values.seatNumber}
                      onChange={handleChange}
                      isValid={touched.seatNumber && !errors.seatNumber}
                      isInvalid={touched.seatNumber && !!errors.seatNumber}
                    />
                    <Form.Control.Feedback type="invalid">{errors.seatNumber}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3} className="mb-3 mt-4 pt-2">
                  <Form.Group controlId="validationFormik_confirmedReservation">
                    <Form.Check
                      type="checkbox"
                      label={strings.confirmedReservation}
                      name="confirmedReservation"
                      checked={values.confirmedReservation}
                      onChange={() => setFieldValue("confirmedReservation", !values.confirmedReservation)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group controlId="validationFormik_anotherAgency">
                    <Form.Check
                      type="checkbox"
                      label={strings.anotherAgency}
                      name="anotherAgency"
                      checked={values.anotherAgency}
                      onChange={() => setFieldValue("anotherAgency", !values.anotherAgency)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className={"d-flex"}>
                <div className={"justify-content-start fw-bold"}>
                  <h5>{strings.totalPrice}: <span>{totalPrice.toFixed(2)}</span></h5>
                </div>
                <button type="submit" className="btn btn-primary ms-auto justify-content-end">{strings.submit}</button>
              </div>
              <hr />
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};

export default AddClientToExcursionForm;